import { useRef, useState, useEffect } from "react";

import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

import FarmCardStyle from "../../assets/stylesheets/FarmCard.module.css";
import CategoryCard from "./CategoryCard";

import farmstay from "../../assets/img/farmstay.webp";
import AnimalFarm from "../../assets/img/animal.webp";
import FamilyFriendly from "../../assets/img/familyfriendly.webp";
import FarmersMarket from "../../assets/img/farmersmarket.webp";
import Festival from "../../assets/img/farmfestival.webp";
import PYOFarm from "../../assets/img/pyopicking.webp";
import Weddings from "../../assets/img/wedding.webp";
import Winery from "../../assets/img/winery.webp";
import Cider from "../../assets/img/cider.webp";
import Photography from "../../assets/img/photography.webp";
import TreeFarm2 from "../../assets/img/treegarm.webp";


import { getCategories } from "../../api/farm-service/categoryService";

import Logger from "../../utils/logger";

const log = Logger.create("CategoriesCarousel");

// image sample
const imageMap = {
  pyo_farm: PYOFarm,
  animal_farm: AnimalFarm,
  tree_farm: TreeFarm2,
  kids_play_area: FamilyFriendly,
  farmers_market: FarmersMarket,
  farmstays: farmstay,
  festivals: Festival,
  weddings: Weddings,
  photography: Photography,
  winery: Winery,
  cidery: Cider
};

const categoryCarouselContainerTitle = "Explore what’s out there";
const categoryCarouselContainerSubtitle = "Explore farms by what they offer";

export default function TopEventsCarousel() {

  const navigate                      = useNavigate();
  const swiperRef                     = useRef(null);
  const prevRef                       = useRef(null);
  const nextRef                       = useRef(null);
  const [categories, setCategories]   = useState([]);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd]             = useState(false);

  const updateNavState = (swiper) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  // Update swiper navigation state on slide change
  useEffect(() => {
    const swiper = swiperRef.current?.swiper;
    if (!swiper) return;
    const onChange = () => {
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
    };
    swiper.on("slideChange", onChange);
    onChange();
    return () => swiper.off("slideChange", onChange);
  }, [categories]);


  // Get Categories
  useEffect(() => {
    (async () => {
      try {
        const res = await getCategories();
        setCategories(res.data);
      } catch (err) {
        log.error("Failed to load categories", err);
      }
    })();
  }, []);
  
  return (
    <section className={FarmCardStyle["events-section"]}>
      <div className={FarmCardStyle["events-container"]}>
        <div className={FarmCardStyle["events-header"]}>
          <div>
            <h2 className={FarmCardStyle["events-title"]}>
              {categoryCarouselContainerTitle}
            </h2>
            <p className={FarmCardStyle["events-subtitle"]}>
              {categoryCarouselContainerSubtitle}
            </p>
          </div>
          <div className={FarmCardStyle["event-card-arrow"]}>
            <button
              ref={prevRef}
              className={`${FarmCardStyle["swiper-prev"]} ${
                isBeginning ? FarmCardStyle["swiper-disabled"] : ""
              }`}
              disabled={isBeginning}
            >
              <ChevronLeft className={FarmCardStyle["custom-arrow-icon"]} />
            </button>
            <button
              ref={nextRef}
              className={`${FarmCardStyle["swiper-next"]} ${
                isEnd ? FarmCardStyle["swiper-disabled"] : ""
              }`}
              disabled={isEnd}
            >
              <ChevronRight className={FarmCardStyle["custom-arrow-icon"]} />
            </button>
          </div>
        </div>

        <div className={FarmCardStyle["events-carousel-wrapper"]}>
          <Swiper
            ref={swiperRef}
            modules={[FreeMode, Navigation]}
            freeMode={true}
            spaceBetween={10}
            slidesPerView={3}   
            breakpoints={{
              768:  { slidesPerView: 3 },   
              1024: { slidesPerView: 5 }    
            }}
            onBeforeInit={swiper => {
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
              updateNavState(swiper)
            }}          
            onSlideChange={(swiper) => updateNavState(swiper)}
            className={FarmCardStyle["swiper-carousel"]}
          >
          {categories.map((category) => (
            <SwiperSlide key={category.id}>
              <div
                className={FarmCardStyle["cursor-pointer"]}
                onClick={() => navigate(`/category/${category.id}`)}
              >
              <CategoryCard event={{
                id: category.id,
                name: category.name,
                imageUrl: imageMap[category.name.toLowerCase().replace(/\s/g, "_")],
                title: category.name,        
              }} />              
              </div>
            </SwiperSlide>
          ))}
          </Swiper>
        </div>
      </div>
    </section>
  );
}